import React, { useRef, useState } from 'react';
import JSZip from 'jszip';

const ODGImporter = ({ onImportComplete }) => {
  const fileInputRef = useRef(null);
  const [generatedComponent, setGeneratedComponent] = useState(null);
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      // Read ODG file
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      
      // Debug: List all files in the ODG
      console.log("Files in ODG:", Object.keys(contents.files));
      
      // Get content.xml
      const contentXml = await contents.file('content.xml').async('text');
      
      // Debug: Print first part of the content to see structure
      console.log("Content XML preview:", contentXml.substring(0, 500));
      
      // Parse XML
      const parser = new DOMParser();
      const doc = parser.parseFromString(contentXml, 'application/xml');
      
      // Debug: Log all custom shapes
      const allShapes = doc.querySelectorAll('draw\\:custom-shape, draw\\:path, draw\\:polygon');
      console.log(`Found ${allShapes.length} shapes in the ODG file`);
      
      allShapes.forEach((shape, index) => {
        const shapeId = shape.getAttribute('draw:id') || shape.getAttribute('draw:name') || `shape-${index}`;
        console.log(`Shape ${index}: ${shapeId}, type: ${shape.nodeName}`);
        
        // Log path data if available
        const path = shape.querySelector('draw\\:enhanced-path');
        if (path) {
          console.log(`  Path data: ${path.getAttribute('draw:enhanced-path')}`);
        }
      });
      
      // Find first page
      const pages = doc.getElementsByTagName('draw:page');
      if (pages.length === 0) {
        alert('No pages found in the ODG file.');
        return;
      }
      
      // Generate JSX directly
      const pageName = pages[0].getAttribute('draw:name') || 'ODGPage';
      const cleanName = pageName.replace(/[^a-zA-Z0-9]/g, '');
      
      // Start building pure JSX
      let jsx = `import React from 'react';\n\n`;
      jsx += `const ${cleanName} = () => {\n`;
      jsx += `  return (\n`;
      
      // Add wrapper div with page name
      jsx += `    <div className="${pageName}" style={{ position: 'relative' }}>\n`;
      
      // Process all elements in the page to JSX (with extra indentation because of wrapper)
      jsx += processElements(pages[0], 6);
      
      // Close wrapper div
      jsx += `    </div>\n`;
      
      // Close the component
      jsx += `  );\n};\n\n`;
      jsx += `export default ${cleanName};`;
      
      // Set state
      setGeneratedComponent(jsx);
      
      // Create a temporary textarea for easy copying 
      const textarea = document.createElement('textarea');
      textarea.value = jsx;
      document.body.appendChild(textarea);
      textarea.select();
      
      // Try to copy to clipboard
      try {
        document.execCommand('copy');
        alert('JSX code has been copied to clipboard!\n\nYou can now paste it anywhere you need.');
      } catch (error) {
        alert('Could not automatically copy the JSX. Please use the Copy button below the code.');
      }
      
      // Remove the textarea
      document.body.removeChild(textarea);
      
    } catch (error) {
      alert('Error: ' + error.message);
    }
    
    event.target.value = null;
  };
  
  // Process all elements to pure JSX
  const processElements = (parentElement, indentLevel) => {
    let jsx = '';
    const indent = ' '.repeat(indentLevel);
    
    // Get all draw elements
    const elements = Array.from(parentElement.children).filter(el => 
      el.nodeName.toLowerCase().startsWith('draw:')
    );
    
    // First, process all elements to objects for easier manipulation
    const processedElements = [];
    
    elements.forEach(element => {
      // Get ID and component info
      const elementId = element.getAttribute('draw:id') || 
                       element.getAttribute('draw:name') || '';
      
      // Skip if no ID
      if (!elementId) return;
      
      // Parse component name from ID (Format: Name-Component)
      const parts = elementId.split('-');
      if (parts.length < 2) return;
      
      const name = parts.slice(0, -1).join('-');
      const componentName = parts[parts.length - 1];
      
      // Create processed element object
      processedElements.push({
        element,
        name,
        componentName,
        style: extractStyles(element),
        text: extractTextContent(element),
        id: elementId,
        children: element.children
      });
    });
    
    // Now process the elements into JSX, handling BG components specially
    for (let i = 0; i < processedElements.length; i++) {
      const currentEl = processedElements[i];
      
      // Skip BG components completely - they're just style containers
      if (currentEl.componentName === 'BG') {
        console.log(`Skipping BG component: ${currentEl.name}-BG`);
        continue;
      }
      
      console.log(`Processing element: ${currentEl.name}-${currentEl.componentName}`);
      
      // Start JSX element
      jsx += `${indent}<${currentEl.componentName} name="${currentEl.name}"`;
      
      // Look for a matching BG component that has same name prefix
      const bgComponent = processedElements.find(el => 
        el.componentName === 'BG' && 
        el.name === currentEl.name
      );
      
      // Add style props, merging with BG if found
      let style = {...currentEl.style};
      
      if (bgComponent) {
        console.log(`Found matching BG for ${currentEl.name}: using its styles`);
        // Merge BG styles, giving priority to the BG styles
        style = {...style, ...bgComponent.style};
      }
      
      if (Object.keys(style).length > 0) {
        // Format with one style prop per line for better readability
        let styleStr = Object.entries(style)
          .map(([key, value]) => `    ${key}: "${value}"`)
          .join(',\n' + indent);
        
        jsx += ` style={{\n${indent}${styleStr}\n${indent}}}`;
      }
      
      // Add text if available
      if (currentEl.text) {
        jsx += ` text="${currentEl.text}"`;
      }
      
      // Check for SVG content if this is an SVGButton
      let svgContent = '';
      
      if (currentEl.componentName === 'SVGButton') {
        // Debug log
        console.log(`Processing SVGButton: ${currentEl.name}`);
        
        // Different ways SVG content might be stored in ODG
        
        // 1. First try direct custom shapes in the button
        const customShapes = Array.from(currentEl.element.querySelectorAll('draw\\:custom-shape, draw\\:path, draw\\:polygon'));
        
        if (customShapes.length > 0) {
          console.log(`Found ${customShapes.length} shapes directly in button ${currentEl.name}`);
          svgContent = extractSVGFromShapes(customShapes, indent);
        } else {
          console.log(`No direct shapes in button ${currentEl.name}, checking parent document`);
          
          // 2. Try to find shapes by ID (sometimes they're defined elsewhere)
          // Look for shapes with IDs that start with the button name
          const shapeId = `${currentEl.name}-shape`;
          const externalShapes = Array.from(currentEl.element.ownerDocument.querySelectorAll(`[draw\\:id="${shapeId}"], [draw\\:name="${shapeId}"]`));
          
          if (externalShapes.length > 0) {
            console.log(`Found ${externalShapes.length} external shapes for button ${currentEl.name}`);
            svgContent = extractSVGFromShapes(externalShapes, indent);
          } else {
            // 3. If button name references another component (like "close"), look for that
            const allShapes = Array.from(currentEl.element.ownerDocument.querySelectorAll('draw\\:custom-shape, draw\\:path, draw\\:polygon'));
            console.log(`Searching through ${allShapes.length} total shapes for button ${currentEl.name}`);
            
            // Look for shapes with names or IDs that might match this button
            const matchingShapes = allShapes.filter(shape => {
              const shapeId = shape.getAttribute('draw:id') || shape.getAttribute('draw:name') || '';
              return shapeId.toLowerCase().includes(currentEl.name.toLowerCase());
            });
            
            if (matchingShapes.length > 0) {
              console.log(`Found ${matchingShapes.length} matching shapes for button ${currentEl.name}`);
              svgContent = extractSVGFromShapes(matchingShapes, indent);
            } else {
              // Use an empty SVG with no comments (JSX doesn't like HTML comments)
              svgContent = `${indent}  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">\n`;
              svgContent += `${indent}    <rect x="10" y="10" width="80" height="80" style={{ fill: "none", stroke: "currentColor" }} />\n`;
              svgContent += `${indent}  </svg>`;
              
              // Log the issue instead of adding comments
              console.log(`No SVG data found for ${currentEl.name} button`);
              console.log(`Available tags: ${Array.from(currentEl.element.children).map(c => c.nodeName).join(', ')}`);
            }
          }
        }
      }
      
      // Get all children EXCEPT BGs with the same name prefix
      const childElements = Array.from(currentEl.element.children).filter(child => {
        // Get child info
        const childId = child.getAttribute('draw:id') || child.getAttribute('draw:name') || '';
        if (!childId) return false;
        
        const childParts = childId.split('-');
        if (childParts.length < 2) return false;
        
        const childName = childParts.slice(0, -1).join('-');
        const childType = childParts[childParts.length - 1];
        
        // Filter out BG components with the same name prefix
        return !(childType === 'BG' && childName === currentEl.name);
      });
      
      // If we have SVG content or children, make a container element
      if (svgContent || childElements.length > 0) {
        jsx += `>\n`;
        
        // Add SVG content if any
        if (svgContent) {
          jsx += svgContent + '\n';
        }
        
        // Process children (we've already filtered out BG with same name)
        if (childElements.length > 0) {
          jsx += processElements(currentEl.element, indentLevel + 2);
        }
        
        jsx += `${indent}</${currentEl.componentName}>\n`;
      } else {
        // Self-closing
        jsx += ` />\n`;
      }
    }
    
    return jsx;
  };
  
  // Helper to extract component name and info
  const getComponentInfo = (element) => {
    const elementId = element.getAttribute('draw:id') || 
                      element.getAttribute('draw:name') || '';
    
    // If no ID, return default
    if (!elementId) return { name: '', componentName: '' };
    
    // Parse component name from ID (Format: Name-Component)
    const parts = elementId.split('-');
    if (parts.length < 2) return { name: elementId, componentName: '' };
    
    return {
      name: parts.slice(0, -1).join('-'),
      componentName: parts[parts.length - 1],
      id: elementId
    };
  };
  
  // Helper to extract text content
  const extractTextContent = (element) => {
    const textElements = element.getElementsByTagName('text:p');
    if (textElements.length > 0) {
      return textElements[0].textContent || '';
    }
    return '';
  };
  
  // Extract SVG from various shape elements
  const extractSVGFromShapes = (shapes, indent) => {
    console.log(`Extracting SVG from ${shapes.length} shapes`);
    
    // Start SVG
    let svg = `${indent}  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">\n`;
    
    // Process each shape
    shapes.forEach((shape, index) => {
      console.log(`Processing shape ${index}: ${shape.nodeName}`);
      const nodeName = shape.nodeName.toLowerCase();
      
      // Extract style for the shape
      const shapeStyle = extractStyles(shape);
      let styleProps = [];
      
      if (Object.keys(shapeStyle).length > 0) {
        // Convert CSS to SVG styles
        Object.entries(shapeStyle).forEach(([key, value]) => {
          if (key === 'backgroundColor') styleProps.push(`fill: "${value}"`);
          else if (key === 'borderColor') styleProps.push(`stroke: "${value}"`);
          else if (key === 'borderWidth') styleProps.push(`strokeWidth: "${value}"`);
          else if (key === 'opacity') styleProps.push(`opacity: "${value}"`);
          else styleProps.push(`${key}: "${value}"`);
        });
      }
      
      // Add default styles if needed
      if (!styleProps.some(prop => prop.startsWith('stroke:'))) {
        styleProps.push('stroke: "currentColor"');
      }
      if (!styleProps.some(prop => prop.startsWith('strokeWidth:'))) {
        styleProps.push('strokeWidth: "2"');
      }
      if (!styleProps.some(prop => prop.startsWith('fill:'))) {
        styleProps.push('fill: "none"');
      }
      
      // Format style props with one per line
      const styleStr = styleProps
        .map(prop => `${indent}      ${prop}`)
        .join(',\n');
      
      // Process based on shape type
      if (nodeName === 'draw:custom-shape') {
        console.log(`Custom shape found: ${shape.getAttribute('draw:id') || shape.getAttribute('draw:name') || 'unnamed'}`);
        
        // First, try to find enhanced-path
        const enhancedPath = shape.querySelector('draw\\:enhanced-path');
        if (enhancedPath) {
          const pathData = enhancedPath.getAttribute('draw:enhanced-path');
          console.log(`Found enhanced path: ${pathData}`);
          
          if (pathData) {
            svg += `${indent}    <path d="${convertEnhancedPathToSVG(pathData)}" \n`;
            svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
          }
        } else {
          // If no enhanced path, check for other path definitions
          // Try draw:path
          const drawPath = shape.querySelector('draw\\:path');
          if (drawPath) {
            const pathData = drawPath.getAttribute('d') || drawPath.getAttribute('svg:d');
            console.log(`Found draw:path with data: ${pathData}`);
            
            if (pathData) {
              svg += `${indent}    <path d="${pathData}" \n`;
              svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
            }
          } else {
            // Try svg:path
            const svgPath = shape.querySelector('svg\\:path');
            if (svgPath) {
              const pathData = svgPath.getAttribute('d');
              console.log(`Found svg:path with data: ${pathData}`);
              
              if (pathData) {
                svg += `${indent}    <path d="${pathData}" \n`;
                svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
              }
            } else {
              // If still no path, check if this is a regular shape
              const shapeType = shape.getAttribute('draw:shape-type');
              console.log(`Shape type: ${shapeType}`);
              
              if (shapeType === 'rectangle') {
                svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
                svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
              } else if (shapeType === 'ellipse') {
                svg += `${indent}    <ellipse cx="50" cy="50" rx="40" ry="40" \n`;
                svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
              } else {
                // Check for specific geometry elements
                const geometry = shape.querySelector('draw\\:enhanced-geometry');
                if (geometry) {
                  const type = geometry.getAttribute('draw:type');
                  console.log(`Found enhanced geometry of type: ${type}`);
                  
                  // Convert common LibreOffice Draw shapes to SVG
                  if (type === 'rectangle') {
                    svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
                    svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                  } else if (type === 'ellipse') {
                    svg += `${indent}    <ellipse cx="50" cy="50" rx="40" ry="40" \n`;
                    svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                  } else if (type === 'triangle') {
                    svg += `${indent}    <polygon points="50,10 10,90 90,90" \n`;
                    svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                  } else {
                    // For other types, extract path data if available
                    const equationType = geometry.getAttribute('draw:enhanced-path');
                    if (equationType) {
                      svg += `${indent}    <path d="${convertEnhancedPathToSVG(equationType)}" \n`;
                      svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                    } else {
                      // Generic fallback rectangle (no comments)
                      svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
                      svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                    }
                  }
                } else {
                  // Fallback rectangle (no comments)
                  svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
                  svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
                }
              }
            }
          }
        }
      } 
      else if (nodeName === 'draw:path') {
        // Get SVG path
        const svgPath = shape.getAttribute('svg:d') || shape.getAttribute('d');
        console.log(`Found draw:path with SVG path data: ${svgPath}`);
        
        if (svgPath) {
          svg += `${indent}    <path d="${svgPath}" \n`;
          svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
        } else {
          // Try for other path attributes
          const enhancedPath = shape.getAttribute('draw:enhanced-path');
          if (enhancedPath) {
            svg += `${indent}    <path d="${convertEnhancedPathToSVG(enhancedPath)}" \n`;
            svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
          } else {
            // Fallback with no comments
            svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
            svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
          }
        }
      } 
      else if (nodeName === 'draw:polygon') {
        // Get polygon points
        const points = shape.getAttribute('draw:points') || shape.getAttribute('points');
        console.log(`Found polygon with points: ${points}`);
        
        if (points) {
          const svgPoints = points.replace(/,/g, ' ');
          svg += `${indent}    <polygon points="${svgPoints}" \n`;
          svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
        } else {
          // Fallback with no comments
          svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
          svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
        }
      }
      else {
        // Unknown shape type
        console.log(`Unknown shape type: ${nodeName}`);
        // Fallback shape without comments
        svg += `${indent}    <rect x="10" y="10" width="80" height="80" \n`;
        svg += `style={{\n${styleStr}\n${indent}    }} />\n`;
      }
    });
    
    // If no shapes were properly processed, add a fallback
    if (!svg.includes('<path') && !svg.includes('<rect') && !svg.includes('<ellipse') && !svg.includes('<polygon')) {
      // Add fallback shape without comments
      svg += `${indent}    <rect x="10" y="10" width="80" height="80" `;
      svg += `style={{ fill: "none", stroke: "currentColor" }} />\n`;
    }
    
    // Close SVG
    svg += `${indent}  </svg>`;
    
    return svg;
  };
  
  // Helper function to convert ODG enhanced path to SVG path
  const convertEnhancedPathToSVG = (enhancedPath) => {
    console.log(`Converting enhanced path: ${enhancedPath}`);
    
    // LibreOffice Draw uses a similar but not identical syntax to SVG paths
    // Here we try to convert the most common commands
    
    // Step 1: Replace ODF-specific tokens
    let svgPath = enhancedPath
      // Path commands (mostly the same in ODG and SVG)
      .replace(/M/g, 'M')    // Move to (absolute)
      .replace(/m/g, 'm')    // Move to (relative)
      .replace(/L/g, 'L')    // Line to (absolute)
      .replace(/l/g, 'l')    // Line to (relative)
      .replace(/C/g, 'C')    // Cubic bezier (absolute)
      .replace(/c/g, 'c')    // Cubic bezier (relative)
      .replace(/S/g, 'S')    // Smooth cubic bezier (absolute)
      .replace(/s/g, 's')    // Smooth cubic bezier (relative)
      .replace(/Q/g, 'Q')    // Quadratic bezier (absolute)
      .replace(/q/g, 'q')    // Quadratic bezier (relative)
      .replace(/T/g, 'T')    // Smooth quadratic bezier (absolute)
      .replace(/t/g, 't')    // Smooth quadratic bezier (relative)
      .replace(/A/g, 'A')    // Arc (absolute)
      .replace(/a/g, 'a')    // Arc (relative)
      .replace(/Z/g, 'Z')    // Close path
      .replace(/z/g, 'z');   // Close path
    
    // Step 2: Handle ODG-specific commands that don't exist in SVG
    // These might need more advanced parsing based on the actual ODG path content
    
    // Check if we have any ODG-specific commands and add a comment if so
    const unsupportedCommands = /[BDEFGHJKNOPRVWXY]/g;
    if (unsupportedCommands.test(enhancedPath)) {
      console.warn("Enhanced path contains unsupported ODG-specific commands");
    }
    
    return svgPath;
  };
  
  // Extract styles from element
  const extractStyles = (element) => {
    const styles = {};
    
    // Get position and size
    const x = element.getAttribute('svg:x');
    const y = element.getAttribute('svg:y');
    const width = element.getAttribute('svg:width');
    const height = element.getAttribute('svg:height');
    
    if (x) styles.left = x;
    if (y) styles.top = y;
    if (width) styles.width = width;
    if (height) styles.height = height;
    
    // Get style name reference
    const styleName = element.getAttribute('draw:style-name');
    if (styleName) {
      // Find the style in automatic-styles
      const autoStyles = element.ownerDocument.getElementsByTagName('office:automatic-styles');
      if (autoStyles.length > 0) {
        const styleElements = autoStyles[0].getElementsByTagName('style:style');
        
        // Find matching style
        for (let i = 0; i < styleElements.length; i++) {
          const style = styleElements[i];
          if (style.getAttribute('style:name') === styleName) {
            // Found the style - extract properties
            
            // Graphic properties (fill, stroke, etc)
            const graphicProps = style.getElementsByTagName('style:graphic-properties');
            if (graphicProps.length > 0) {
              const props = graphicProps[0];
              
              // Background color
              const fillColor = props.getAttribute('draw:fill-color');
              if (fillColor) styles.backgroundColor = fillColor;
              
              // Border color and width
              const strokeColor = props.getAttribute('svg:stroke-color');
              if (strokeColor) styles.borderColor = strokeColor;
              
              const strokeWidth = props.getAttribute('svg:stroke-width');
              if (strokeWidth) styles.borderWidth = strokeWidth;
              
              // Fill type (none, solid, etc)
              const fillType = props.getAttribute('draw:fill');
              if (fillType === 'none') {
                styles.backgroundColor = 'transparent';
              }
              
              // Opacity
              const opacity = props.getAttribute('draw:opacity');
              if (opacity) styles.opacity = opacity;
              
              // Border style
              const strokeStyle = props.getAttribute('draw:stroke');
              if (strokeStyle) {
                if (strokeStyle === 'none') {
                  styles.border = 'none';
                } else if (strokeStyle === 'dash') {
                  styles.borderStyle = 'dashed';
                }
              }
              
              // Padding
              const padding = props.getAttribute('fo:padding');
              if (padding) styles.padding = padding;
            }
            
            // Text properties (font, size, color)
            const textProps = style.getElementsByTagName('style:text-properties');
            if (textProps.length > 0) {
              const props = textProps[0];
              
              // Font size
              const fontSize = props.getAttribute('fo:font-size');
              if (fontSize) styles.fontSize = fontSize;
              
              // Text color
              const color = props.getAttribute('fo:color');
              if (color) styles.color = color;
              
              // Font family
              const fontFamily = props.getAttribute('fo:font-family') || props.getAttribute('style:font-family-complex');
              if (fontFamily) {
                // Clean up font family (remove quotes if present)
                styles.fontFamily = fontFamily.replace(/['"]/g, '');
              }
              
              // Font weight
              const fontWeight = props.getAttribute('fo:font-weight');
              if (fontWeight) styles.fontWeight = fontWeight;
              
              // Font style (italic)
              const fontStyle = props.getAttribute('fo:font-style');
              if (fontStyle) styles.fontStyle = fontStyle;
              
              // Text decoration (underline, line-through)
              const textDecoration = props.getAttribute('style:text-underline-style') || 
                                    props.getAttribute('style:text-line-through-style');
              if (textDecoration && textDecoration !== 'none') {
                styles.textDecoration = textDecoration === 'solid' ? 'underline' : textDecoration;
              }
              
              // Text transform (uppercase, lowercase)
              const textTransform = props.getAttribute('fo:text-transform');
              if (textTransform) styles.textTransform = textTransform;
              
              // Letter spacing
              const letterSpacing = props.getAttribute('fo:letter-spacing');
              if (letterSpacing) styles.letterSpacing = letterSpacing;
            }
            
            // Paragraph properties
            const paraProps = style.getElementsByTagName('style:paragraph-properties');
            if (paraProps.length > 0) {
              const props = paraProps[0];
              
              // Text alignment
              const textAlign = props.getAttribute('fo:text-align');
              if (textAlign) styles.textAlign = textAlign;
              
              // Line height
              const lineHeight = props.getAttribute('fo:line-height');
              if (lineHeight) styles.lineHeight = lineHeight;
              
              // Margins
              const marginLeft = props.getAttribute('fo:margin-left');
              if (marginLeft) styles.marginLeft = marginLeft;
              
              const marginRight = props.getAttribute('fo:margin-right');
              if (marginRight) styles.marginRight = marginRight;
              
              const marginTop = props.getAttribute('fo:margin-top');
              if (marginTop) styles.marginTop = marginTop;
              
              const marginBottom = props.getAttribute('fo:margin-bottom');
              if (marginBottom) styles.marginBottom = marginBottom;
            }
            
            break;
          }
        }
      }
    }
    
    // Check if we're handling a Label component - add specific checks
    const elementId = element.getAttribute('draw:id') || element.getAttribute('draw:name') || '';
    if (elementId.includes('-Label')) {
      console.log(`Processing Label element: ${elementId}`);
      
      // Directly look for text elements to extract styles
      const textElements = element.getElementsByTagName('text:p');
      if (textElements.length > 0) {
        // Look for specific text styling inline
        const spans = textElements[0].getElementsByTagName('text:span');
        if (spans.length > 0) {
          // Extract style from span's style-name
          const spanStyleName = spans[0].getAttribute('text:style-name');
          if (spanStyleName) {
            // Try to find span style
            const textStyles = element.ownerDocument.querySelectorAll(`[style\\:name="${spanStyleName}"]`);
            if (textStyles.length > 0) {
              const textStyle = textStyles[0];
              const props = textStyle.getElementsByTagName('style:text-properties')[0];
              
              if (props) {
                // Font size
                const fontSize = props.getAttribute('fo:font-size');
                if (fontSize) styles.fontSize = fontSize;
                
                // Text color
                const color = props.getAttribute('fo:color');
                if (color) styles.color = color;
                
                // Font family
                const fontFamily = props.getAttribute('fo:font-family');
                if (fontFamily) styles.fontFamily = fontFamily.replace(/['"]/g, '');
                
                // Font weight
                const fontWeight = props.getAttribute('fo:font-weight');
                if (fontWeight) styles.fontWeight = fontWeight;
                
                // Font style
                const fontStyle = props.getAttribute('fo:font-style');
                if (fontStyle) styles.fontStyle = fontStyle;
              }
            }
          }
        }
      }
    }
    
    return styles;
  };
  
  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".odg"
        onChange={handleFileUpload}
      />
      <button 
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        style={{
          padding: '8px 16px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        Import ODG
      </button>
      
      {generatedComponent && (
        <div style={{marginTop: '20px'}}>
          <h4>Generated JSX:</h4>
          <div style={{
            display: 'flex', 
            gap: '10px', 
            marginBottom: '15px'
          }}>
            <button
              onClick={() => {
                navigator.clipboard.writeText(generatedComponent)
                  .then(() => alert('JSX copied to clipboard!'))
                  .catch(err => alert('Failed to copy: ' + err));
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Copy to Clipboard
            </button>
            
            <button
              onClick={() => {
                const blob = new Blob([generatedComponent], {type: 'text/plain'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'generated-component.jsx';
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Download as JSX
            </button>
            
            <textarea
              id="jsx-output"
              value={generatedComponent}
              readOnly
              style={{
                position: 'absolute',
                left: '-9999px',
                height: '1px',
                width: '1px'
              }}
            />
          </div>
          
          <pre style={{
            background: '#f5f5f5',
            padding: '15px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            {generatedComponent}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ODGImporter;